import { useRouter } from "next/router";
import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "../../core/translation.core";

export interface RouteChangeDialogProps {
  title: string;
  children: ReactNode;
  shouldConfirmLeave: boolean;
  permittedPaths?: string[];
}

export const RouteChangeDialog = ({
  title,
  children,
  shouldConfirmLeave,
  permittedPaths,
}: RouteChangeDialogProps): ReactElement<RouteChangeDialogProps> => {
  const { t, msg } = useTranslation();
  const [shouldShowLeaveConfirmDialog, setShouldShowLeaveConfirmDialog] = useState(false);
  const [nextRouterPath, setNextRouterPath] = useState<string>();

  const { push, events } = useRouter();

  const onRouteChangeStart = useCallback(
    (nextPath: string) => {
      const nextPathname = nextPath.split("?")[0];

      if (!shouldConfirmLeave || permittedPaths!.includes(nextPathname)) {
        return;
      }

      setShouldShowLeaveConfirmDialog(true);
      setNextRouterPath(nextPath);

      throw "cancelRouteChange";
    },
    [shouldConfirmLeave, permittedPaths]
  );

  const onRejectRouteChange = () => {
    setNextRouterPath(undefined);
    setShouldShowLeaveConfirmDialog(false);
  };

  const onConfirmRouteChange = () => {
    setShouldShowLeaveConfirmDialog(false);
    // simply remove the listener here so that it doesn't get triggered when we push the new route.
    // This assumes that the component will be removed anyway as the route changes
    removeListener();
    push(nextRouterPath!);
  };

  const removeListener = () => {
    events.off("routeChangeStart", onRouteChangeStart);
  };

  useEffect(() => {
    events.on("routeChangeStart", onRouteChangeStart);

    return removeListener;
    // eslint-disable-next-line
  }, [onRouteChangeStart]);

  return (
    <Dialog
      title={title}
      confirmLabel={t(msg.checkout.continueLater)}
      cancelLabel={t(msg.checkout.cancel)}
      isOpen={shouldShowLeaveConfirmDialog}
      onConfirm={onConfirmRouteChange}
      onReject={onRejectRouteChange}
    >
      {children}
    </Dialog>
  );
};

interface DialogProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  isOpen: boolean;
  onConfirm: () => void;
  onReject: () => void;
}
export const Dialog = ({ title, children, confirmLabel, cancelLabel, isOpen, onConfirm, onReject }: DialogProps) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!modalRef || !modalRef.current) return;
      const container = modalRef.current as any;
      const isInside = container.contains(e.target);

      if (!isInside) {
        onReject();
      }
    };

    window.addEventListener("mouseup", handleClick);

    return () => window.removeEventListener("mouseup", handleClick);
  }, [modalRef, onReject]);

  return (
    <section
      className={`${isOpen ? "block" : "hidden"} z-[200] fixed top-0 left-0 w-full h-full bg-[rgba(0,0,0,0.45)]`} //z-index of 200 because paypal buttons have at least 199
    >
      <div
        ref={modalRef}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[450px] p-12 bg-gray-200 z-10 opacity-1 flex flex-col items-center justify-center text-center space-y-4"
      >
        <p className="mb-6">{title}</p>
        {children}
        <div className="flex gap-3">
          <button onClick={onConfirm} className="outline px-5 py-2">
            {confirmLabel}
          </button>
          <button onClick={onReject} className="outline px-3 py-2 bg-primary-dark text-white">
            {cancelLabel}
          </button>
        </div>
      </div>
    </section>
  );
};
